import API from '@aws-amplify/api';

/**
 * Invokes a GET request to `endpoint`. If successful, returns a response object
 * of type RestApiResponse containing information about the request, including
 * the HTTP `status` and the response body as the `data` object (of type T).
 *
 * If there is an error, an exception is thrown (of type RestApiError) that
 * includes a standard error "message" field. The response (if there is one)
 * will be available in the "response" field. In most cases, errors should be
 * dealt with at the query-level using the error support provided by React
 * Query.
 */
export async function get<T>(
  endpoint: string,
  query?: { [key: string]: string },
  headers?: { [key: string]: string },
): Promise<RestApiResponse<T>> {
  return await API.get(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
    queryStringParameters: query,
    headers,
    response: true, //return the entire Axios response object instead of only response.data)
  });
}

/*
 * Invokes a POST request to `endpoint`. If successful, returns a response object
 * of type RestApiResponse containing information about the request, including
 * the HTTP `status` and the response body as the `data` object (of type T).
 *
 * If there is an error, an exception is thrown (of type RestApiError) that
 * includes a standard error "message" field. The response (if there is one)
 * will be available in the "response" field. Errors should be dealt with at the
 * query-level using the onError callback in useMutation (React Query).
 */
export async function post<T, S>(endpoint: string, body: S): Promise<RestApiResponse<T>> {
  return await API.post(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
    body,
    response: true, //return the entire Axios response object instead of only response.data)
  });
}

/*
 * Invokes a PUT request to `endpoint`. If successful, returns a response object
 * of type RestApiResponse containing information about the request, including
 * the HTTP `status` and the response body as the `data` object (of type T).
 *
 * If there is an error, an exception is thrown (of type RestApiError) that
 * includes a standard error "message" field. The response (if there is one)
 * will be available in the "response" field. Errors should be dealt with at the
 * query-level using the onError callback in useMutation (React Query).
 */
export async function put<T, S = void>(endpoint: string, body?: S): Promise<RestApiResponse<T>> {
  return await API.put(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
    body,
    response: true, //return the entire Axios response object instead of only response.data)
  });
}

/*
 * Invokes a PATCH request to `endpoint`. If successful, returns a response object
 * of type RestApiResponse containing information about the request, including
 * the HTTP `status` and the response body as the `data` object (of type T).
 *
 * If there is an error, an exception is thrown (of type RestApiError) that
 * includes a standard error "message" field. The response (if there is one)
 * will be available in the "response" field. Errors should be dealt with at the
 * query-level using the onError callback in useMutation (React Query).
 */
export async function patch<T, S = void>(endpoint: string, body?: S): Promise<RestApiResponse<T>> {
  return await API.patch(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
    body,
    response: true, //return the entire Axios response object instead of only response.data)
  });
}

/*
 * Invokes a DELETE request to `endpoint`. If successful, returns a response object
 * of type RestApiResponse containing information about the request, including
 * the HTTP `status` and the response body as the `data` object (of type T).
 *
 * If there is an error, an exception is thrown (of type RestApiError) that
 * includes a standard error "message" field. The response (if there is one)
 * will be available in the "response" field. Errors should be dealt with at the
 * query-level using the onError callback in useMutation (React Query).
 */
export async function del<T, S = void>(endpoint: string, body?: S): Promise<RestApiResponse<T>> {
  return await API.del(import.meta.env.VITE_API_GATEWAY_NAME, endpoint, {
    body,
    response: true, //return the entire Axios response object instead of only response.data)
  });
}

export interface RestApiResponse<T> {
  data?: T;
  status?: number;
  statusText?: string;
  config?: {
    url: string;
  };
}

export interface RestApiError {
  response?: RestApiResponse<ErrorDetails>;
  message?: string;
}

export interface ErrorDetails {
  message?: string; // This should be consolidated/fixed when we can agree on
  errorMessage?: string; // consistent error format.
  errors?: string[] | ErrorMessageWrapper[];
}

export interface ErrorMessageWrapper {
  message: string;
}

/**
 * Extracts error message(s) from a RestApiError.
 * Checks response.data.errors array and extracts messages from each error item.
 * For string errors, extracts the message directly. For object errors, extracts the detail field.
 * If an error message contains "Error:", only the part after "Error:" is returned.
 * If multiple errors are found, they are formatted as a bulleted list.
 * Returns empty string if no error message is found.
 */
export function getRestApiErrorMessage(error: RestApiError): string {
  const errorData = error.response?.data;
  if (!errorData) {
    return '';
  }

  if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
    const errorMessages = errorData.errors
      .map(errorItem => {
        let message: string | null = null;
        if (typeof errorItem === 'string') {
          message = errorItem;
        } else if (typeof errorItem === 'object' && errorItem?.detail) {
          message = errorItem.detail;
        }

        if (message) {
          const errorIndex = message.indexOf('Error:');
          if (errorIndex !== -1) {
            return message.substring(errorIndex + 'Error:'.length).trim();
          }
          return message;
        }
        return null;
      })
      .filter((msg): msg is string => msg !== null);

    if (errorMessages.length > 0) {
      return errorMessages.map(msg => `• ${msg}`).join('\n');
    }
  }

  return '';
}
